import React, { useEffect, useMemo, useState } from 'react';
import { Button, Table, Spinner, Row, Col, Badge, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAll, inactivateUser, activateUser } from '../../services/Users/Users';
import UserModal from '../../components/Configs/UserModal';

function Users() {
  // const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtros (como Consumibles)
  const [statusFilter, setStatusFilter] = useState('active'); // ✅ default: solo activos
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);

  // paginación
  const [page, setPage] = useState(1);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const list = await getAll();
      setUsers(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar los usuarios.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  // reset page al cambiar filtros
  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, pageSize]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return users
      .filter((u) => {
        if (statusFilter === 'active') return !!u.isActive;
        if (statusFilter === 'inactive') return !u.isActive;
        return true; // all
      })
      .filter((u) => {
        if (!q) return true;
        const haystack = [
          u.userName,
          u.userRoles?.name,
          u.id,
        ]
          .map((x) => String(x ?? '').toLowerCase())
          .join(' ');
        return haystack.includes(q);
      });
  }, [users, statusFilter, search]);

  const pageCount = Math.ceil(filtered.length / pageSize);
  const safePageCount = pageCount; // puede ser 0 si no hay registros
  const currentPage = safePageCount === 0 ? 0 : Math.min(page, safePageCount);

  const pagedRows = useMemo(() => {
    if (safePageCount === 0) return [];
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize, safePageCount]);

  // handlers
  const openAdd = () => {
    setModalMode('add');
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (id) => {
    setModalMode('edit');
    setEditId(id);
    setShowModal(true);
  };

  const handleEdit = (id) => openEdit(id);
  const addUserRedirect = () => openAdd();

  const setActiveState = async (id, makeActive) => {
    try {
      const res = makeActive ? await activateUser(id) : await inactivateUser(id);
      if (!res) {
        toast.error('Lo sentimos, algo salió mal.');
        return;
      }
      toast.success('Usuario actualizado correctamente');
      fetchUsers();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo actualizar el usuario.');
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-0">Usuarios</h2>
        </div>

        <Button variant="primary" onClick={addUserRedirect}>
          Agregar
        </Button>
      </div>

      {/* Filtros */}
      <Row className="g-3 align-items-end mb-3">
        <Col md={3}>
          <Form.Label>Estado</Form.Label>
          <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
            <option value="all">Todos</option>
          </Form.Select>
        </Col>

        <Col md={6}>
          <Form.Label>Buscar</Form.Label>
          <Form.Control
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o rol..."
          />
        </Col>

        <Col md={3}>
          <Form.Label>Por página</Form.Label>
          <Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            {[10, 20, 30, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Tabla */}
      <div className="table-rounded-wrapper">
        <div className="table-responsive">
          <Table bordered hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th className="col-nowrap" hidden='true'>#</th>
                <th className="actions-col">Acciones</th>
                <th>Nombre</th>
                <th>Tipo de usuario</th>
                <th>Estatus</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando...
                  </td>
                </tr>
              ) : pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    Sin registros
                  </td>
                </tr>
              ) : (
                pagedRows.map((u) => (
                  <tr key={u.id}>
                    <td className="col-nowrap" hidden='true'>{u.id}</td>
                    <td className="actions-col">
                      <div className="actions-cell">
                        <Button variant="outline-primary" size="sm" onClick={() => handleEdit(u.id)}>
                          Editar
                        </Button>

                        {u.isActive ? (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => setActiveState(u.id, false)}
                          >
                            Inactivar
                          </Button>
                        ) : (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => setActiveState(u.id, true)}
                          >
                            Activar
                          </Button>
                        )}
                      </div>
                    </td>

                    <td>{u.userName}</td>
                    <td>{u.userRoles?.name ?? 'N/D'}</td>
                    <td>
                      {u.isActive ? (
                        <Badge bg="success">Activo</Badge>
                      ) : (
                        <Badge bg="danger">Inactivo</Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Paginación igual a Consumibles */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Página {currentPage} de {safePageCount} ({filtered.length} registros)
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePageCount === 0 || currentPage <= 1}
          >
            Anterior
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(safePageCount, p + 1))}
            disabled={safePageCount === 0 || currentPage >= safePageCount}
          >
            Siguiente
          </Button>
        </div>
      </div>

      {/* Modal */}
      <UserModal
        show={showModal}
        mode={modalMode}
        userId={editId}
        onHide={() => setShowModal(false)}
        onSaved={() => {
          setShowModal(false);
          fetchUsers();
        }}
      />
    </>
  );
}

export default Users